import { Alert } from "@common/controls/Alert/component";
import { type ButtonProps } from "@common/controls/Button/component";
import CancelButton from "@common/controls/Form/Buttons/Cancel";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormField } from "@common/controls/Form/component";
import TextInput from "@common/controls/Inputs/Text/component";
import { SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { type I18n } from "@common/lib/i18n";
import { type NavigateEventDetail, NavigationEvent } from "@common/services/navigation";
import { Common } from "@common/types";
import classNames from "classnames";
import { identity, isFunction } from "lodash";
import * as React from "react";
import { FormModalControlled, type FormModalControlledRenderParams } from "../Form/component";
import withShow from "../hocs/withShow";
import withSubmit, { type WithSubmitProps } from "../hocs/withSubmit";

export type ConfirmTypeCustom = Partial<ButtonProps> & {
  confirmType: string;
};
export type ConfirmType = "delete" | "reset" | "restore" | "confirm" | "risk" | ConfirmTypeCustom;
export type ChildrenPosition = "beforeConfirm" | "afterConfirm";

export type ConfirmChildrenData = {
  disabledTooltip?: string;
};

export interface ConfirmModalProps {
  /** Whether to show text input or not */
  alert?: string;
  cannotConfirm?: boolean;
  children?: ((data: ConfirmChildrenData) => any) | React.JSX.Element | string;
  childrenPosition?: ChildrenPosition;
  className?: string;
  cancelCaption?: string;
  confirmCaption?: string;
  defaultChildrenData?: any;
  errorMessage?: string | null;
  hideHeaderWhenSubmitting?: boolean;
  icon?: string;
  isConfirming?: boolean;
  isStrict?: boolean;
  isVisible: boolean;
  preventCloseWhenSubmitting?: boolean;
  message?: string;
  onChange?: (name: string, value: any) => void;
  onCancel?: () => void;
  onClose: () => void;
  onConfirm: (data: object) => void;
  title?: string | null;
  toast?: string;
  type?: ConfirmType;
}

const getButtonProps = (i18n: I18n, type: ConfirmType, props: ConfirmModalProps): Partial<ButtonProps> => {
  if (type === "delete") {
    return {
      icon: props.icon,
      type: "danger",
      children: props.confirmCaption ?? i18n.delete
    };
  }

  if (type === "reset") {
    return {
      icon: props.icon,
      type: "danger",
      children: props.confirmCaption ?? i18n.reset
    };
  }

  if (type === "restore") {
    return {
      icon: props.icon,
      type: "danger",
      children: props.confirmCaption ?? i18n.restore
    };
  }

  if (type === "confirm") {
    return {
      icon: props.icon,
      type: "primary",
      children: props.confirmCaption ?? i18n.confirm
    };
  }

  if (type === "risk") {
    return {
      icon: props.icon,
      type: "danger",
      children: props.confirmCaption ?? i18n.proceed
    };
  }

  return type;
};

const getConfirmType = (type: ConfirmType): string => (typeof type === "string" ? type : type.confirmType);

type ConfirmModalData = Common.GenericMap<any> & {
  __confirmText: string;
};
const ConfirmModal = (props: ConfirmModalProps) => {
  const i18n = useI18n();

  const {
    cannotConfirm,
    children,
    confirmCaption,
    isConfirming,
    isStrict,
    message,
    title = i18n.confirm,
    type = "confirm",
    ...modalProps
  } = props;

  const canConfirm = React.useCallback(
    (confirmText: string) => (!isStrict || getConfirmType(type).toUpperCase() === confirmText) && !cannotConfirm,
    [cannotConfirm, isStrict, type]
  );

  const defaultValues = React.useMemo(
    (): ConfirmModalData => ({
      ...props.defaultChildrenData,
      _confirm: ""
    }),
    [props.defaultChildrenData]
  );

  if (!props.isVisible) return null; // NOTE: we could pass this prop down to FormModal, but in that case the state can be preserved, which is not desirable

  return (
    <FormModalControlled
      {...modalProps}
      className={classNames(modalProps.className, "confirm_modal")}
      defaultValues={defaultValues}
      submit={props.onConfirm}
      title={title}
    >
      {({ values }: FormModalControlledRenderParams<ConfirmModalData>) => (
        <>
          {props.alert && <Alert color={SystemColor.Orange}>{props.alert}</Alert>}
          {message && <p dangerouslySetInnerHTML={{ __html: message }} />}

          {props.childrenPosition === "beforeConfirm" && children
            ? isFunction(children)
              ? children(values as any)
              : children
            : null}

          {!!isStrict && (
            <FormField
              name="__confirmText"
              value={values.__confirmText}
              Component={TextInput}
              label={i18n.typeVARToConfirm.replace("VAR", getConfirmType(type).toUpperCase())}
              componentProps={{ autoFocus: true }}
              qa="Confirm"
            />
          )}

          {(!props.childrenPosition || props.childrenPosition === "afterConfirm") && children
            ? isFunction(children)
              ? children(values as any)
              : children
            : null}

          <FormButtons>
            <CancelButton onClick={props.onCancel} caption={props.cancelCaption} />
            <SubmitButton
              isDisabled={isConfirming || !canConfirm(values.__confirmText) || !!values.disabledTooltip}
              {...getButtonProps(i18n, type, props)}
              iconPosition="left"
              tooltip={values.disabledTooltip}
            />
          </FormButtons>
        </>
      )}
    </FormModalControlled>
  );
};

export default ConfirmModal;

export const ConfirmModalWithShow = withShow<ConfirmModalProps>(ConfirmModal);

export type ConfirmModalWithSubmitProps = Omit<
  ConfirmModalProps,
  "childrenPosition" | "confirmCaption" | "defaultChildrenData" | "onConfirm" | "children" | "onClose"
> &
  WithSubmitProps<any> & {
    defaultFieldsData?: any;
    fieldsPosition?: ChildrenPosition;
    fields?: (data: any) => any;
    message: any;
    confirmCaption?: string;
  };
export const ConfirmModalWithSubmit = withSubmit<any, any, ConfirmModalWithSubmitProps>(
  ({
    defaultFieldsData,
    fields,
    fieldsPosition,
    isSubmitting,
    onClose,
    submit,
    ...props
  }: ConfirmModalWithSubmitProps) => (
    <ConfirmModal
      {...props}
      children={fields}
      childrenPosition={fieldsPosition}
      confirmCaption={props.confirmCaption}
      defaultChildrenData={defaultFieldsData}
      isConfirming={isSubmitting}
      onClose={onClose ?? identity}
      onConfirm={submit}
    />
  )
);
export const ConfirmModalWithShowSubmit = withShow(ConfirmModalWithSubmit);

export type WithConfirm = <T>(fn: () => void, areEqual?: (defaultValues: T, values: T) => boolean) => void;

export type ConfirmProviderModalProps = {
  onClose: () => void;
  onConfirm: () => void;
  isVisible: boolean;
  message: string;
  title: string;
  type?: ConfirmType;
  isAdmin?: boolean;
};
export type ConfirmProviderModal = React.ComponentType<ConfirmProviderModalProps>;

type ChildrenArgs = {
  withConfirm: WithConfirm;
};
type ConfirmModalProviderProps = {
  children: (args: ChildrenArgs) => any;
  confirmType?: ConfirmTypeCustom;
  ConfirmModal?: ConfirmProviderModal;
  hasChanges: boolean;
  modalClassName?: string;
  modalTitle?: string;
  modalMessage: string;
};
export const ConfirmModalProvider = (props: ConfirmModalProviderProps) => {
  const i18n = useI18n();

  const [confirmAction, setConfirmAction] = React.useState<{
    action: () => void;
  } | null>(null);

  const onConfirm = React.useCallback(() => {
    setConfirmAction(null);
    confirmAction!.action();
  }, [confirmAction, setConfirmAction]);

  const onConfirmModalClose = React.useCallback(() => setConfirmAction(null), [setConfirmAction]);

  const Modal = props.ConfirmModal ?? ConfirmModal;

  React.useEffect(() => {
    if (!props.hasChanges) return;

    const onNavigate = (e: CustomEvent<NavigateEventDetail>) => {
      if (!e.cancelable) return;

      e.preventDefault();

      setConfirmAction({
        action: () => {
          NavigationEvent.dispatch(e.detail, false);
        }
      });
    };

    document.body.addEventListener(NavigationEvent.NAME, onNavigate, true);

    return () => {
      document.body.removeEventListener(NavigationEvent.NAME, onNavigate, true);
    };
  }, [props.hasChanges]);

  const withConfirm = React.useCallback(
    (action) => {
      if (props.hasChanges) {
        setConfirmAction({
          action
        });
      } else {
        action();
      }
    },
    [props.hasChanges]
  );

  return (
    <React.Fragment>
      {props.children({ withConfirm })}

      <Modal
        onConfirm={onConfirm}
        isVisible={!!confirmAction}
        onClose={onConfirmModalClose}
        className={props.modalClassName}
        message={props.modalMessage}
        title={props.modalTitle ?? i18n.confirmation}
        toast=""
        type={props.confirmType}
      />
    </React.Fragment>
  );
};

export type UnsavedChangesConfirmModalProviderProps = Omit<ConfirmModalProviderProps, "modalTitle" | "modalMessage"> & {
  ConfirmModal?: ConfirmProviderModal;
};
export const UnsavedChangesConfirmModalProvider = (props: UnsavedChangesConfirmModalProviderProps) => {
  const i18n = useI18n();

  const confirmType = React.useMemo(
    (): ConfirmTypeCustom => ({
      confirmType: "warn",
      label: i18n.leave,
      type: "danger"
    }),
    []
  );

  return (
    <ConfirmModalProvider
      {...props}
      confirmType={confirmType}
      modalMessage={i18n.infoUnsavedChanges}
      modalTitle={i18n.unsavedChanges}
    />
  );
};
